import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import ExternalLink from "../../../../components/ExternalLink";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";

const AxurIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const formInitialValues = {
    api_key: "",
  };
  const formCheckoutSchema = yup.object().shape({
    api_key: yup.string().required("required"),
  });

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("axur");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { registerAxurIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerAxurIntegration(values.api_key)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Axur integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading && !isIntegratedLoading
            ? "Once integrated, the Breeze platform will automatically read the configurations from Axur and prioritize the suggested remediation's."
            : ""
        }
        image={logos.AxurLogo}
        name={"Axur"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Axur is integrated!</Typography>
            </Box>
          ) : (
            <div>
              <Box>
                {guideMode && (
                  <Box sx={{ mb: 2 }}>
                    <IntegrationGuideAlert />
                  </Box>
                )}
                <Formik
                  onSubmit={onSubmit}
                  initialValues={formInitialValues}
                  validationSchema={formCheckoutSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <GuidelineStepByStep>
                        <GuidelineStep title="Login into your Axur account" />
                        <GuidelineStep title="Generate an API key">
                          <ExternalLink
                            href={
                              "https://one.axur.com/preferences?tab=api-keys"
                            }
                            text="An API KEY can be created via the AXUR platform, in my preferences under the"
                            linkText={" API KEY tab."}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Paste the API key here">
                          <Box width={700}>
                            <Box display="grid" mb="20px" gap="10px">
                              <TextField
                                fullWidth
                                variant="filled"
                                type={showSecret ? "text" : "password"}
                                name="api_key"
                                autoComplete="off"
                                label="API Key"
                                value={values.api_key}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={!!touched.api_key && !!errors.api_key}
                                helperText={touched.api_key && errors.api_key}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle secret visibility"
                                        onClick={handleClickShowSecret}
                                        onMouseDown={handleMouseDownSecret}
                                      >
                                        {showSecret ? (
                                          <Visibility />
                                        ) : (
                                          <VisibilityOff />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                disabled={guideMode}
                              />
                            </Box>
                            <Box display="flex" justifyContent="center">
                              <PrimaryButton type="submit" disabled={guideMode}>
                                Connect
                              </PrimaryButton>
                            </Box>
                          </Box>
                        </GuidelineStep>
                      </GuidelineStepByStep>
                    </Form>
                  )}
                </Formik>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};

export default AxurIntegration;
